import React, { useState, useRef, useEffect } from 'react';
import { X, Table, Users, FileText, Building, Gavel, UserPlus, Tag, BookOpen, File, Link, Database, Vote, UserCheck, Shield, GripVertical } from 'lucide-react';

// Get icon for table based on name patterns
const getTableIcon = (tableName) => {
  const name = tableName.toLowerCase();
  
  // Check for patterns in table name
  if (name.includes('bills_master') || name.includes('bills_11') || name.includes('bills_12')) return Table;
  if (name.includes('actions')) return Gavel;
  if (name.includes('amendments')) return FileText;
  if (name.includes('committees')) return Building;
  if (name.includes('congressmen') || name.includes('members')) return Users;
  if (name.includes('cosponsors')) return UserPlus;
  if (name.includes('subjects')) return Tag;
  if (name.includes('summaries')) return BookOpen;
  if (name.includes('text_versions')) return File;
  if (name.includes('related_bills') || name.includes('member_bills')) return Link;
  if (name.includes('rollcalls')) return Vote;
  if (name.includes('users')) return UserCheck;
  
  // Default
  return Database;
};

const Sidebar = ({ selectedTable, setSelectedTable, sidebarOpen, setSidebarOpen, tableNames }) => {
  const [sidebarWidth, setSidebarWidth] = useState(288); // 18rem = 288px
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const startResizing = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const stopResizing = () => {
    setIsResizing(false);
  };

  const resize = (e) => {
    if (isResizing && sidebarRef.current) {
      const newWidth = e.clientX;
      // Min: 240px (15rem), Max: 480px (30rem) - prevents overlap and too narrow
      if (newWidth >= 240 && newWidth <= 480) {
        setSidebarWidth(newWidth);
      }
    }
  };

  const resetWidth = () => {
    setSidebarWidth(288); // Reset to default 18rem
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResizing);
      return () => {
        document.removeEventListener('mousemove', resize);
        document.removeEventListener('mouseup', stopResizing);
      };
    }
  }, [isResizing]);

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        ref={sidebarRef}
        className={`
          fixed lg:static inset-y-0 left-0 z-30 bg-white dark:bg-marble-800 shadow-2xl transform transition-all duration-300 ease-in-out relative
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isResizing ? '' : 'border-r-2 border-primary-600/30'}
        `}
        style={{ width: `${sidebarWidth}px` }}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header - match main header height exactly */}
          <div className="px-6 py-5 border-b-2 border-primary-600/50">
            <div className="flex items-center justify-between h-12">
              <h2 className="text-lg font-bold text-gray-800 dark:text-primary-200 transition-colors">Tables</h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-primary-400 hover:text-primary-200 lg:hidden transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Table list */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {tableNames.map((tableName) => {
              const Icon = getTableIcon(tableName);
              const isSelected = selectedTable === tableName;
              
              return (
                <button
                  key={tableName}
                  onClick={() => handleTableSelect(tableName)}
                  className={`
                    w-full flex items-center px-4 py-3 rounded-lg text-left transition-all duration-200
                    ${isSelected 
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-200 border-l-4 border-primary-500 shadow-lg' 
                      : 'text-gray-600 dark:text-marble-400 hover:bg-gray-100 dark:hover:bg-marble-700/50 hover:text-primary-600 dark:hover:text-primary-300 border-l-4 border-transparent'
                    }
                  `}
                >
                  <div className="flex items-center justify-center w-5 h-5 mr-3 flex-shrink-0">
                    <Icon className={`w-full h-full transition-colors ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-marble-500'}`} />
                  </div>
                  <span className="text-sm font-medium truncate">{tableName}</span>
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-6 border-t-2 border-primary-600/50 bg-gray-100 dark:bg-marble-900/50 transition-colors">
            <div className="text-xs text-gray-600 dark:text-marble-400 text-center transition-colors">
              <p className="text-primary-600 dark:text-primary-300 font-semibold text-sm">{tableNames.length} Tables Total</p>
              <p className="mt-2 text-gray-500 dark:text-marble-500">Azure SQL Database</p>
              <p className="mt-3 text-xs text-gray-500 dark:text-marble-600">
                Powered by <span className="text-primary-600 dark:text-primary-400 font-semibold">Politheon</span>
              </p>
            </div>
          </div>
        </div>

        {/* Resize handle - only on desktop */}
        <div
          onMouseDown={startResizing}
          onDoubleClick={resetWidth}
          className={`
            hidden lg:block absolute top-0 right-0 h-full w-1 cursor-col-resize group hover:bg-primary-500/50 transition-colors z-50
            ${isResizing ? 'bg-primary-500' : 'bg-primary-600/30'}
          `}
          title="Drag to resize sidebar, double-click to reset"
        >
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <GripVertical className="h-6 w-6 text-primary-400" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
